import React from 'react'
import {
  useOthers,
  useMyPresence,
  isLiveblocksEligible,
} from '@/lib/liveblocks'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useAuth } from '@/hooks/useAuth'
import { useIsRoomActive } from '@/components/multiplayer/RoomShell'
import { UserBadge } from './UserBadge'

/**
 * Full-canvas SVG overlay that renders every peer's cursor. Sits
 * above interactive content (pointer-events: none) so it never
 * intercepts clicks. Each cursor carries a small name-colour pill
 * anchored below the pointer tip.
 *
 * Use inside a <RoomProvider>. Parents are responsible for
 * calling `useMyPresence({ cursor })` on pointer move so remote
 * peers receive our position.
 */
export const PresenceCursors: React.FC = () => {
  const others = useOthers()

  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-40"
      width="100%"
      height="100%"
    >
      {others.map(other => {
        const presence = other.presence as {
          cursor: { x: number; y: number } | null
          userName: string
          userColor: string
        }
        if (!presence?.cursor) return null
        const { x, y } = presence.cursor
        const color = presence.userColor || '#888'
        const name = presence.userName || `User ${other.connectionId}`
        return (
          <g key={other.connectionId} transform={`translate(${x} ${y})`}>
            <path
              d="M0 0 L0 16 L4 12 L7 18 L10 16 L7 10 L12 10 Z"
              fill={color}
              stroke="white"
              strokeWidth={1}
            />
            <rect
              x={14}
              y={10}
              rx={4}
              width={Math.max(40, name.length * 6)}
              height={18}
              fill={color}
            />
            <text
              x={18}
              y={22}
              fontSize={10}
              fontFamily="monospace"
              fill="white"
            >
              {name}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

/**
 * Header avatar stack — up to 3 peers rendered side-by-side, with
 * a "+N" chip when there are more. `self` badges the current user
 * first so their own colour is reinforced.
 */
export const PresenceDock: React.FC = () => {
  const others = useOthers()
  const [self] = useMyPresence()

  const visible = others.slice(0, 3)
  const overflow = others.length - visible.length

  return (
    <div className="flex items-center gap-1">
      {self && (
        <UserBadge
          name={self.userName || 'You'}
          color={self.userColor || '#888'}
          self
          title={`${self.userName} (you)`}
        />
      )}
      {visible.map(other => {
        const p = other.presence as {
          userName?: string
          userColor?: string
        }
        return (
          <UserBadge
            key={other.connectionId}
            name={p.userName || `User ${other.connectionId}`}
            color={p.userColor || '#888'}
          />
        )
      })}
      {overflow > 0 && (
        <span className="inline-flex items-center justify-center rounded-full bg-surface-sunken border border-brd-subtle text-[10px] font-mono text-content-tertiary w-[22px] h-[22px]">
          +{overflow}
        </span>
      )}
    </div>
  )
}

/**
 * Eligibility-gated wrappers — safe to drop anywhere in the tree.
 * Render `null` when the user isn't on a tier that unlocks
 * multiplayer, so consumers don't have to repeat the gate. Inside
 * a `<RoomProvider>` they expose the real presence UI.
 *
 * The conditions match `RoomShell` *exactly* so these never mount
 * their `useOthers` / `useMyPresence` hooks when the upstream
 * provider wasn't rendered (which would throw
 * "RoomProvider is missing from the React tree.").
 */
function shouldRenderPresence(
  user: { id?: string } | null | undefined,
  features: { teamSharing?: boolean } | null | undefined,
): boolean {
  if (!user) return false
  if (!user.id) return false
  return isLiveblocksEligible(features)
}

export const MaybePresenceDock: React.FC = () => {
  const { features } = useEffectiveFeatures()
  const { user } = useAuth()
  // Eligibility check is the "should there be a provider?" question.
  // `useIsRoomActive` is the "is there one right now?" answer — any
  // mismatch (unsaved project, failed Liveblocks auth, tier-override
  // flipping mid-render) would cause `useOthers`/`useMyPresence` to
  // throw without this second gate.
  const roomActive = useIsRoomActive()
  if (!roomActive || !shouldRenderPresence(user, features)) return null
  return <PresenceDock />
}

export const MaybePresenceCursors: React.FC = () => {
  const { features } = useEffectiveFeatures()
  const { user } = useAuth()
  const roomActive = useIsRoomActive()
  if (!roomActive || !shouldRenderPresence(user, features)) return null
  return <PresenceCursors />
}
